import "./App.css";
import { About } from "./views/About";
import { QueryClient, QueryClientProvider } from "react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { NoMatch } from "./views/errors/404.js";
import { TestCricketPlayers } from "./views/cricket/TestPlayers";
import { TestCricketProfile } from "./views/cricket/TestProfile";
import { HowToMaximiseBettingAccumulators } from "./views/betting/HowToMaximiseBettingAccumulators";
import { Home } from "./views/Home";
import { NavBar } from "./views/Utility";
import { DynamicD3 } from "./views/visualisation/DynamicD3";
import { Playground } from "./views/development/Playground";
import { HelmetProvider } from 'react-helmet-async';
import { BreakSimulation } from "./views/sportsModels/englishPool/BreakSimulation";
import { PoolSimulationIntroduction } from "./views/sportsModels/englishPool/PoolSimulationIntroduction";
import { AccuracySimulation } from "./views/sportsModels/englishPool/AccuracySimulation";
import ScrollToTop from "./ScrollToTop";
import { InterestingLines } from "./views/IntersectingLines";
import { PoolGlossary } from "./views/sportsModels/englishPool/PoolGlossary";
import { FoulSimulation } from "./views/sportsModels/englishPool/FoulSimulation";
import { PoolMatches } from "./views/pool/PoolMatches";
import { PoolPlayer } from "./views/pool/PoolPlayer";
import { PoolGroup } from "./views/pool/PoolGroup";
import { DesigningAnalyticsSchemas } from "./views/DesigningAnalyticsSchemas";
import { PoolPredictions } from "./views/pool/PoolPredictions";
import { BettingHome } from "./views/betting/BettingHome";
import { HowToFindValueBettingOnSports } from "./views/betting/HowToFindValueBettingOnSports";
import { UnderstandSportsBetting } from "./views/betting/UnderstandSportsBetting";
import { SportsModelsHome } from "./views/sportsModels/SportsModelsHome";
import { LeagueScheduleGenerator } from "./views/sportsModels/LeagueScheduleGenerator.js";
import { PlayerModel } from "./views/sportsModels/englishPool/PlayerModel";
import { BankrollManagementStrategies } from "./views/betting/BankrollManagementStrategies";
import { KellyCriterion } from "./views/betting/BankrollManagement/KellyCriterion";
import { SportsRankingMethods } from "./views/sportsModels/SportsRankingMethods";
import { PoolLeagueSimulation } from "./views/sportsModels/englishPool/LeagueSimulation";
import { MonteCarloSimulation } from "./views/sportsModels/MonteCarloSimulation";
import { GolfHome } from "./views/sportsModels/golf/GolfHome";
import { GolfRound } from "./views/sportsModels/golf/GolfRound";
import { GolfMethodology } from "./views/sportsModels/golf/GolfMethodology";
import { GolfProfiles } from "./views/sportsModels/golf/GolfProfiles";
import { HoleVisualisation } from "./views/sportsModels/golf/HoleVisualisation";
import { GolferDataModel } from "./views/sportsModels/golf/GolferDataModel";
import { CustomPoolLeagueSimulation } from "./views/sportsModels/englishPool/CustomLeagueSimulation";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Login } from "./pages/Login";
import { Admin } from "./pages/Admin";
import { AddMatch } from "./pages/AddMatch";
import { RatingsExplanation } from "./views/pool/RatingsExplanation";
import { DuckDB } from "./pages/DuckDB";
import { SteamGames } from "./views/steam/SteamGames";
import { SteamInsights } from "./views/steam/SteamInsights";
import { GamesHome } from "./views/games/GamesHome";
import { SnakesAndLadders } from "./views/games/snakesAndLadders/SnakesAndLadders";
import { NonogramSolver } from "./views/games/nonogramSolver/NonogramSolver";
const HexMapBuilder = process.env.NODE_ENV === 'development'
  ? require('./views/sportsModels/golf/components/HexMapBuilder').HexMapBuilder
  : null;

// Create query client
const queryClient = new QueryClient();

function App() {
	return (
		<HelmetProvider>
			<AuthProvider>
				<BrowserRouter>
					<ScrollToTop>
						<QueryClientProvider client={queryClient}>
							<Routes>
								<Route path="/" element={<NavBar />}>
									<Route index element={<Home />} />
									<Route path="login" element={<Login />} />
									<Route path="admin" element={
										<ProtectedRoute>
											<Admin />
										</ProtectedRoute>
									} />
									<Route path="admin/add-match" element={
										<ProtectedRoute>
											<AddMatch />
										</ProtectedRoute>
									} />
									<Route path="about" element={<About />} />

									<Route path="cricket" element={<TestCricketPlayers />} />
									<Route path="cricket/player/:playerName" element={<TestCricketProfile />} />
									<Route path="data-visualisation/dynamic-d3-components" element={<DynamicD3 />} />

									<Route path="betting" element={<BettingHome />} />
									<Route path="betting/understand-sports-betting" element={<UnderstandSportsBetting />} />
									<Route path="betting/how-to-find-value-betting-on-sports" element={<HowToFindValueBettingOnSports />} />
									<Route path="betting/how-to-maximise-sports-betting-accumulators" element={<HowToMaximiseBettingAccumulators />} />
									<Route path="betting/sports-betting-bankroll-management-strategies" element={<BankrollManagementStrategies />} />
									<Route path="betting/kelly-criterion-bankroll-management" element={<KellyCriterion />} />

									<Route path="sports-models" element={<SportsModelsHome />} />
									<Route path="sports-models/league-schedule-generator" element={<LeagueScheduleGenerator />} />
									<Route path="sports-models/sports-ranking-methods-colley-massey-elo" element={<SportsRankingMethods />} />
									<Route path="sports-models/monte-carlo-simulation-sports-prediction" element={<MonteCarloSimulation />} />

									{/* Pool Monte Carlo Simulation */}
									<Route path="sports-models/english-pool-monte-carlo-simulation" element={<PoolSimulationIntroduction />} />
									<Route path="sports-models/english-pool-monte-carlo-simulation/shot-accuracy" element={<AccuracySimulation />} />
									<Route path="sports-models/english-pool-monte-carlo-simulation/break-efficiency" element={<BreakSimulation />} />
									<Route path="sports-models/english-pool-monte-carlo-simulation/foul-impact" element={<FoulSimulation />} />
									<Route path="sports-models/english-pool-monte-carlo-simulation/player-model" element={<PlayerModel />} />
									<Route path="sports-models/english-pool-monte-carlo-simulation/league-predictions" element={<PoolLeagueSimulation />} />
									<Route path="sports-models/english-pool-monte-carlo-simulation/league-predictions/custom" element={<CustomPoolLeagueSimulation />} />
									<Route path="sports-models/english-pool-monte-carlo-simulation/glossary" element={<PoolGlossary />} />


									{/* Golf */}
									<Route path="sports-models/golf" element={<GolfHome />} />
									<Route path="sports-models/golf/round-prediction" element={<GolfRound />} />
									<Route path="sports-models/golf/methodology" element={<GolfMethodology />} />
									<Route path="sports-models/golf/profiles" element={<GolfProfiles />} />
									<Route path="sports-models/golf/golfer-data-model" element={<GolferDataModel />} />
									<Route path="sports-models/golf/shot-level-simulation" element={<HoleVisualisation />} />
									<Route path="sports-models/golf/hole-visualisation" element={<Navigate to="/sports-models/golf/shot-level-simulation" />} />

									<Route path="playground" element={<Playground />} />
									<Route path="intersecting-lines" element={<InterestingLines />} />

									<Route path="pool/matches" element={<PoolMatches />} />
									<Route path="pool/player/:playerID" element={<PoolPlayer />} />
									<Route path="pool/group/:groupUrlSlug" element={<PoolGroup />} />
									<Route path="pool/group/:groupUrlSlug/ratings-explanation" element={<RatingsExplanation />} />
									<Route path="pool/predictions/:groupUrlSlug" element={<PoolPredictions />} />

									<Route path="analytics/designing-analytics-schemas" element={<DesigningAnalyticsSchemas />} />

									<Route path="duckdb" element={<DuckDB />} />

									<Route path="steam/games" element={<SteamGames />} />
									<Route path="steam/insights" element={<SteamInsights />} />

									{/* Games */}
									<Route path="games" element={<GamesHome />} />
									<Route path="games/snakes-and-ladders" element={<SnakesAndLadders />} />
									<Route path="games/nonogram-solver" element={<NonogramSolver />} />

									{/* Dev-only tools */}
									{process.env.NODE_ENV === 'development' && HexMapBuilder && (
										<Route path="dev/hole-builder" element={<HexMapBuilder />} />
									)}

									{/* Redirects */}
									<Route path="pool-monte-carlo-simulation" element={<Navigate to="/sports-models/english-pool-monte-carlo-simulation" />} />
									<Route path="pool-monte-carlo-simulation/shot-accuracy" element={<Navigate to="/sports-models/english-pool-monte-carlo-simulation/shot-accuracy" />} />
									<Route path="pool-monte-carlo-simulation/break-efficiency" element={<Navigate to="/sports-models/english-pool-monte-carlo-simulation/break-efficiency" />} />
									<Route path="pool-monte-carlo-simulation/foul-impact" element={<Navigate to="/sports-models/english-pool-monte-carlo-simulation/foul-impact" />} />
									<Route path="pool-monte-carlo-simulation/glossary" element={<Navigate to="/sports-models/english-pool-monte-carlo-simulation/glossary" />} />
									<Route path="concept/how-to-maximise-sports-betting-accumulators" element={<Navigate to="/betting/how-to-maximise-sports-betting-accumulators" />} />
									<Route path="visualisation/dynamic-d3-components" element={<Navigate to="/data-visualisation/dynamic-d3-components" />} />

									{/* Catch anything that is matched above */}
									<Route path="*" element={<NoMatch />} />
								</Route>
							</Routes>
						</QueryClientProvider>
					</ScrollToTop>
				</BrowserRouter>
			</AuthProvider>
		</HelmetProvider>
	);
};

export default App;
